import {
    X,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "preact/hooks";
import {
    Button,
} from "./Button.tsx";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl";

type ModalProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: ComponentChildren;
    size?: ModalSize;
};

const sizeStyles: Record<ModalSize, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
};

export const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    size = "md",
}: ModalProps): ComponentChildren => {
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);
    const backdropRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setVisible(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimating(true);
                });
            });
        } else {
            setAnimating(false);
            const timer = setTimeout(() => setVisible(false), 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose]);

    // close on click anywhere outside the dialog. previously this
    // only fired when the click landed exactly on backdropRef — but
    // backdropRef is the scroll container, with the dialog's own
    // flex parent stacked on top of it visually. clicking on the
    // grey area normally hit the flex child, which never matched
    // backdropRef → click-outside silently did nothing
    const handleBackdropClick = useCallback((event: MouseEvent): void => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        // any descendant of role="dialog" should ignore the close
        if (target.closest("[role=\"dialog\"]")) return;
        onClose();
    }, [onClose]);

    if (!visible) return null;

    return (
        <div className="relative z-50">
            <div
                className={`fixed inset-0 bg-black/60 transition-opacity duration-200 ${
                    animating ? "opacity-100" : "opacity-0"
                }`}
            />
            <div
                ref={backdropRef}
                className="fixed inset-0 overflow-y-auto"
                onClick={handleBackdropClick}
            >
                <div className="flex min-h-full items-center justify-center p-4">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                        className={`
                            w-full ${
                            sizeStyles[size]
                        } transform overflow-hidden rounded-lg
                            bg-bg-secondary border border-border
                            p-6 text-left align-middle shadow-xl
                            transition-all duration-200
                            ${
                            animating
                                ? "opacity-100 scale-100"
                                : "opacity-0 scale-95"
                        }
                        `.trim().replace(/\s+/g, " ")}
                    >
                        {title && (
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-text-primary">
                                    {title}
                                </h3>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="p-1 rounded hover:bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        )}
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

type ConfirmModalVariant = "danger" | "warning" | "info";

type ConfirmModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmModalVariant;
    isLoading?: boolean;
    children?: ComponentChildren;
};

export const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "danger",
    isLoading = false,
    children,
}: ConfirmModalProps): ComponentChildren => {
    const confirmVariant: Record<ConfirmModalVariant, "error" | "primary"> = {
        danger: "error",
        warning: "error",
        info: "primary",
    };

    const handleKeyDown = useCallback((event: KeyboardEvent): void => {
        if (event.key === "Enter" && !isLoading) {
            event.preventDefault();
            onConfirm();
        }
    }, [isLoading, onConfirm]);

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, handleKeyDown]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <p className="text-text-secondary mb-6">{message}</p>
            {children}
            <div className="flex justify-end gap-3">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    {cancelText}
                </Button>
                <Button
                    variant={confirmVariant[variant]}
                    onClick={onConfirm}
                    isLoading={isLoading}
                >
                    {confirmText}
                </Button>
            </div>
        </Modal>
    );
};
